import { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Checkbox,
  FormControl,
  FormGroup,
  FormControlLabel,
} from "@mui/material";
import AppButton from "../buttons/AppButton";
import CustomTypography from "../form/CustomTypography";
import {
  FilterSidebarMenu,
  FilterMenuType,
} from "../../../../models/FilterMenu";
import { useStores } from "../../../../use-stores";
import { ModuleType } from "../../../../models/ModuleType";
import RangeField from "../textFields/RangeField";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import CloseIcon from "@mui/icons-material/Close";
import { GerUserParams } from "../../../../models/User/GetUserParams";
import { useNotification } from "../../../../utils/NotificationsContext";
import AppSearchDropDown2 from "./AppSearchDropdown2";
import AppSearchDropdownMulti from "./AppSearchDropdownMulti";
import AppDropDown from "./AppDropDown";
import { DateRangePickerNew } from "../textFields/DatePickerRangeNew";
import DatePicker2 from "../textFields/DatePicker2";
import dayjs from "dayjs";

interface FilterProps {
  toggleFilter: () => void;
  isOpen: boolean;
  items: FilterSidebarMenu[];
  onApply: () => void;
  handleClear: () => void;
  disablePastDates?: boolean;
  disableFutureDates?: boolean;
}

const FilterSidebar: React.FC<FilterProps> = ({
  toggleFilter,
  isOpen,
  items,
  onApply,
  handleClear,
  disablePastDates = true,
  disableFutureDates = true,
}) => {
  const [dynamicLists, setDynamicLists] = useState<{
    [key: string]: FilterValue[];
  }>({});

  const { showError } = useNotification();

  let apiTimer: any;

  const {
    orderStore,
    customerStore,
    userStore,
    TaskTrackerStore,
    projectManagementStore,
    inventoryStore,
  } = useStores();
  const [_visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setVisible(true), 10);
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  function convertToFilterValue(item: unknown): FilterValue {
    if (typeof item === "object" && item !== null) {
      const typedItem = item as Record<string, unknown>;
      if (typedItem["first_name"] && typedItem["last_name"]) {
        return {
          id: String(typedItem.id),
          value: `${typedItem["first_name"]} ${typedItem["last_name"]}`,
        };
      }

      const valueKeyMapping = ["value", "name", "first_name"];

      for (const key of valueKeyMapping) {
        if (key in typedItem && typeof typedItem[key] === "string") {
          return {
            id: String(typedItem.id),
            value: String(typedItem[key]),
          };
        }
      }
    }

    return {
      id: String(item),
      value: String(item),
    };
  }

  const fetchSearchResults = (
    moduleType: string,
    subType: string,
    searchText: string,
    fieldKey: string,
  ) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2) {
      apiTimer = setTimeout(async () => {
        try {
          let results: any = [];
          switch (moduleType) {
            case ModuleType.ORDER:
              await orderStore.searchOrderIds(searchText);
              results = orderStore.orderIds;
              break;

            case ModuleType.CUSTOMER:
              await customerStore.searchCustomers({
                page_no: 1,
                search_text: searchText,
              });
              results = customerStore.customers;
              break;

            case ModuleType.USER:
              const userFilter: GerUserParams = {};
              userFilter.search_text = searchText;
              await userStore.getUsers(userFilter);
              results = userStore.users;
              break;

            case ModuleType.ADMIN:
              await TaskTrackerStore.getAdminBySearch(searchText);
              results = TaskTrackerStore.admins;
              break;

            case ModuleType.PROJECT_MANAGEMENT:
              await projectManagementStore.getProjectManagementList({
                search_text: searchText,
              });
              results = TaskTrackerStore.admins;
              break;
            case ModuleType.INVENTORY:
              await inventoryStore.searchIds(searchText);
              results = inventoryStore.ids;
              break;

            default:
              return null;
          }

          setDynamicLists((prev) => ({
            ...prev,
            [fieldKey]: results.map(convertToFilterValue),
          }));
        } catch (error) {
          showError("Error", "Something went wrong");
        }
      }, 1500);
    }
  };

  const renderFilters = () => {
    return items.map((filter, index) => {
      switch (filter.fieldType) {
        case FilterMenuType.SEARCH_BAR:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 10 }}
              >
                {filter.label}
              </CustomTypography>
              <AppSearchDropDown2
                isMultiSelect={true}
                title={filter.label}
                value={filter.value}
                list={dynamicLists[filter.label] || []}
                handleSearch={(searchText) =>
                  fetchSearchResults(
                    filter.moduleType,
                    filter.subType!,
                    searchText!,
                    filter.label,
                  )
                }
                handleSelect={filter.onChange}
                key={index}
                local={false}
              />
            </Box>
          );

        case FilterMenuType.MULTI_SELECT:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 10 }}
              >
                {filter.label}
              </CustomTypography>
              <AppSearchDropDown2
                isMultiSelect={true}
                title={filter.label}
                value={filter.value}
                list={dynamicLists[filter.label] || []}
                handleSearch={(searchText) =>
                  fetchSearchResults(
                    filter.moduleType,
                    filter.subType!,
                    searchText!,
                    filter.label,
                  )
                }
                handleSelect={filter.onChange}
                key={index}
                local={false}
              />
            </Box>
          );

        case FilterMenuType.SINGLE_SELECT:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 10 }}
              >
                {filter.label}
              </CustomTypography>
              <AppSearchDropDown2
                isMultiSelect={false}
                title={filter.label}
                value={filter.value}
                list={dynamicLists[filter.label] || []}
                handleSearch={(searchText) =>
                  fetchSearchResults(
                    filter.moduleType,
                    filter.subType!,
                    searchText!,
                    filter.label,
                  )
                }
                handleSelect={filter.onChange}
                key={index}
                local={false}
              />
            </Box>
          );

        case FilterMenuType.LOCAL_SINGLE_SELECT:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 20 }}
              >
                {filter.label}
              </CustomTypography>
              <AppDropDown
                 title={filter.placeholder || ""}
                inlinePlaceholder={filter.inlinePlaceholder}
                isMultiSelect={false}
                value={filter.value?.length > 0 ? filter.value[0].id : ""}
                list={filter.list}
                fontSize={"12px"}
                onSingleSelectionChange={(id) => {
                  const selectedItem = filter.list?.find(
                    (item: FilterValue) => String(item.id) === String(id),
                  );
                  filter.onChange(selectedItem ? [selectedItem] : []);
                }}
              />
            </Box>
          );

        case FilterMenuType.LOCAL_MULTI_SELECT:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 10 }}
              >
                {filter.label}
              </CustomTypography>
              <AppDropDown
                title=""
                isMultiSelect={true}
                valuesList={filter.value}
                list={filter.list}
                fontSize={"12px"}
                onMultiSelectionChange={filter.onChange}
              />
            </Box>
          );

        case FilterMenuType.LOCAL_SEARCH_SINGLE_SELECT:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 10 }}
              >
                {filter.label}
              </CustomTypography>
              <AppSearchDropdownMulti
                title={filter.placeholder || filter.label}
                isMultiSelect={false}
                value={filter.value}
                list={filter.list}
                local={true}
                fontSize={"12px"}
                handleSelect={filter.onChange}
              />
            </Box>
          );

        case FilterMenuType.LOCAL_SEARCH_MULTI_SELECT:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 10 }}
              >
                {filter.label}
              </CustomTypography>
              <AppSearchDropdownMulti
                title={filter.placeholder || filter.label}
                isMultiSelect={true}
                value={filter.value}
                list={filter.list}
                local={true}
                fontSize={"12px"}
                handleSelect={filter.onChange}
              />
            </Box>
          );

        case FilterMenuType.PRICE_RANGE:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 10 }}
              >
                {filter.label}
              </CustomTypography>
              <RangeField
                minimumValue={filter.value.min}
                maximumValue={filter.value.max}
                onValuesChange={(min: string, max: string) => {
                  filter.onChange({ min: Number(min), max: Number(max) });
                }}
              />
            </Box>
          );

        case FilterMenuType.BOOLEAN:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 10 }}
              >
                {filter.label}
              </CustomTypography>
              <FormControl component="fieldset" sx={{ marginTop: "-7px" }}>
                <FormGroup>
                  {filter.list?.map((option: FilterValue) => (
                    <FormControlLabel
                      sx={{ marginBottom: "-7px" }}
                      key={option.id}
                      control={
                        <Checkbox
                          sx={{
                            color: "#6455ED",
                            "&.Mui-checked": {
                              color: "#6455ED",
                            },
                          }}
                          checked={filter.value?.some(
                            (item: FilterValue) =>
                              String(item.id) === String(option.id),
                          )}
                          onChange={(e) => {
                            const updatedValues = e.target.checked
                              ? [
                                  ...filter.value,
                                  { id: option.id, value: option.value },
                                ]
                              : filter.value?.filter(
                                  (item: FilterValue) => item.id !== option.id,
                                );
                            filter.onChange(updatedValues);
                          }}
                        />
                      }
                      label={option.value}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>
          );

        case FilterMenuType.DATE:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 10 }}
              >
                {filter.label}
              </CustomTypography>
              <DatePicker2
                label=""
                type="date"
                onChange={(date) => {
                  filter.onChange(date!);
                }}
                value={filter.value}
                minDate={disablePastDates ? dayjs().startOf("day") : undefined}
                maxDate={disableFutureDates ? dayjs().endOf("day") : undefined}
              />
            </Box>
          );

        case FilterMenuType.DATERANGE:
          return (
            <Box key={index} sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading8"
                color="#7C7A80"
                style={{ marginBottom: 10 }}
              >
                {filter.label}
              </CustomTypography>
              <DateRangePickerNew
                onRangeSelect={filter.onChange}
                startDate={filter.value?.startDate}
                endDate={filter.value?.endDate}
                disableFutureDates={disableFutureDates}
              />
            </Box>
          );

        default:
          return null;
      }
    });
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={toggleFilter}
        ModalProps={{ style: { zIndex: 1300 } }}
        PaperProps={{
          sx: {
            width: 400,
            paddingLeft: 5,
            paddingTop: 2,
            paddingRight: 5,
            paddingBottom: 0,
            scrollbarWidth: "none",
            right: 0,
          },
        }}
      >
        <Box
          sx={{
            height: "100vh",
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Fixed Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              background: "#fff",
              borderBottom: "2px solid #E2E2E2",
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={2}
            >
              <CustomTypography variant="heading3">Filters</CustomTypography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  cursor: "pointer",
                  borderRadius: "50%",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                onClick={toggleFilter}
              >
                <CloseIcon sx={{ fontSize: "18px", color: "#666" }} />
              </Box>
            </Box>
          </Box>

          {/* Scrollable Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              height: "85%",
              marginTop: 2,
              // marginBottom: 2,
              padding: 2,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "3px",
                "&:hover": {
                  background: "#a8a8a8",
                },
              },
              scrollbarWidth: "thin",
              scrollbarColor: "#c1c1c1 #f1f1f1",
            }}
          >
            {renderFilters()}
          </Box>

          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              left: 0,
              width: "100%",
              background: "#fff",
              padding: 3,
              zIndex: 2,
            }}
          >
            <Box display="flex" justifyContent="space-between" gap={2}>
              <AppButton
                variant="grayscale"
                label="Clear Filters"
                customStyles={{ width: "100%" }}
                onClick={() => handleClear()}
              />
              <AppButton
                customStyles={{ width: "100%" }}
                variant="primary"
                label="Apply Filter"
                onClick={() => onApply()}
              />
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default FilterSidebar;
